// Вкладка «Замеры»: обхваты и % жира одной формой (пустые пропускаются), свой замер,
// у каждого замера свой график и список записей.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'

import { TimeSeriesChart } from '@/components/charts/TimeSeriesChart'
import { Button, Card, ErrorText, Input, Modal, Spinner, useToast } from '@/components/ui'
import { cn } from '@/lib/cn'
import { formatDate, formatNumber } from '@/lib/format'
import { todayIso, type Period } from '@/lib/periods'
import { metricKeys, metricsApi, useMetricTypes, useSeries, type MetricType } from '@/pages/metrics/api'
import { dayPoints, fromLocalInput, measurementPayload, measurementTypes, seriesQuery, toLocalInput } from '@/pages/metrics/series'

import { fieldError, formError, useInvalidateMetrics } from './helpers'
import { EmptyCard, SamplesList } from './parts'

function CustomMeasurementDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const client = useQueryClient()
  const toast = useToast()
  const [label, setLabel] = useState('')
  const [unit, setUnit] = useState('см')
  const add = useMutation({
    mutationFn: () => metricsApi.addCustom(label.trim(), unit.trim()),
    onSuccess: (type) => {
      void client.invalidateQueries({ queryKey: metricKeys.types })
      toast.show({ message: `Замер «${type.label_ru}» добавлен`, tone: 'accent' })
      setLabel('')
      onClose()
    },
  })
  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Свой замер"
      description="Появится в форме замеров и получит свой график."
    >
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (label.trim()) add.mutate()
        }}
        noValidate
      >
        <Input
          label="Название"
          autoFocus
          maxLength={60}
          placeholder="Например, предплечье"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          error={fieldError(add.error, 'label_ru') ?? fieldError(add.error, 'key')}
        />
        <Input label="Единица" maxLength={16} value={unit} onChange={(e) => setUnit(e.target.value)} />
        <ErrorText error={formError(add.error, ['label_ru', 'key'])} />
        <Button type="submit" variant="primary" loading={add.isPending} disabled={!label.trim()}>
          Добавить
        </Button>
      </form>
    </Modal>
  )
}

function MeasurementsForm({ types, onCustom }: { types: MetricType[]; onCustom: () => void }) {
  const toast = useToast()
  const invalidate = useInvalidateMetrics()
  const [raw, setRaw] = useState<Record<string, string>>({})
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({})
  const [at, setAt] = useState(() => toLocalInput(new Date()))
  const [note, setNote] = useState('')

  const save = useMutation({
    mutationFn: (values: Record<string, number>) => metricsApi.addBatch(values, fromLocalInput(at), note || null),
    onSuccess: (saved) => {
      void invalidate()
      setRaw({})
      setNote('')
      toast.show({ message: `Сохранено замеров: ${saved.length}`, tone: 'accent' })
    },
  })

  const submit = (e: FormEvent) => {
    e.preventDefault()
    const { values, errors } = measurementPayload(raw)
    setLocalErrors(errors)
    if (Object.keys(errors).length > 0) return
    if (Object.keys(values).length === 0) {
      setLocalErrors({ _: 'Заполни хотя бы один замер' })
      return
    }
    save.mutate(values)
  }

  const fields = types.map((t) => `values.${t.key}`)
  return (
    <Card
      title="Новые замеры"
      subtitle="Заполни только то, что мерил — пустые поля пропускаются."
      actions={
        <Button size="sm" variant="ghost" icon="plus" onClick={onCustom}>
          Свой замер
        </Button>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {types.map((t) => (
            <Input
              key={t.key}
              label={t.label_ru}
              inputMode="decimal"
              suffix={t.unit || undefined}
              value={raw[t.key] ?? ''}
              onChange={(e) => setRaw((r) => ({ ...r, [t.key]: e.target.value }))}
              error={localErrors[t.key] ?? fieldError(save.error, `values.${t.key}`)}
            />
          ))}
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <Input
            label="Дата и время"
            type="datetime-local"
            value={at}
            onChange={(e) => setAt(e.target.value)}
            error={fieldError(save.error, 'measured_at')}
          />
          <Input label="Заметка" maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
        <ErrorText error={localErrors._ ? null : formError(save.error, [...fields, 'measured_at'])}>
          {localErrors._}
        </ErrorText>
        <div>
          <Button type="submit" variant="primary" loading={save.isPending}>
            Сохранить замеры
          </Button>
        </div>
      </form>
    </Card>
  )
}

function MeasurementChart({ type }: { type: MetricType }) {
  const [period, setPeriod] = useState<Period>('3m')
  const today = todayIso()
  const series = useSeries(type.key, seriesQuery(period, today))
  return (
    <>
      {series.isLoading ? (
        <div className="flex h-[220px] items-center justify-center">
          <Spinner />
        </div>
      ) : (
        <TimeSeriesChart
          series={[{ key: type.key, label: type.label_ru, unit: type.unit || undefined, points: dayPoints(series.data?.points ?? []) }]}
          period={period}
          onPeriodChange={setPeriod}
          today={today}
          emptyText="За этот период замеров нет"
        />
      )}
      <ErrorText error={series.error} className="mt-2" />
    </>
  )
}

function LatestTile({ type, active, onClick }: { type: MetricType; active: boolean; onClick: () => void }) {
  const last = useQuery({
    queryKey: [...metricKeys.samples(type.key), 'latest'],
    queryFn: () => metricsApi.samples(type.key, null, 1),
  })
  const latest = last.data?.items[0] ?? null
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex min-h-16 flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors',
        active ? 'border-accent bg-surface-2' : 'border-border hover:bg-surface-2',
      )}
    >
      <span className="text-xs text-muted">{type.label_ru}</span>
      <span className="text-base font-semibold tabular-nums text-fg">
        {latest ? formatNumber(latest.value, { digits: 1 }) : '—'}
        {latest && type.unit ? <span className="text-xs font-normal text-muted"> {type.unit}</span> : null}
      </span>
      <span className="text-xs text-muted">{latest ? formatDate(latest.day) : 'нет записей'}</span>
    </button>
  )
}

export function MeasurementsPage() {
  const types = useMetricTypes()
  const [customOpen, setCustomOpen] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)

  if (types.isLoading) {
    return (
      <div className="flex justify-center py-16">
        <Spinner />
      </div>
    )
  }
  if (types.error) return <ErrorText error={types.error} />

  const list = measurementTypes(types.data ?? [])
  const current = list.find((t) => t.key === selected) ?? list.find((t) => t.key === 'waist_cm') ?? list[0]

  return (
    <div className="flex flex-col gap-4">
      <MeasurementsForm types={list} onCustom={() => setCustomOpen(true)} />
      {current ? (
        <Card title="Графики замеров" subtitle="Выбери замер — у каждого свой график и свои записи.">
          <div className="mb-4 grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-5">
            {list.map((t) => (
              <LatestTile key={t.key} type={t} active={t.key === current.key} onClick={() => setSelected(t.key)} />
            ))}
          </div>
          <MeasurementChart key={current.key} type={current} />
          <h3 className="mb-1 mt-5 text-sm font-semibold text-fg">Записи: {current.label_ru.toLowerCase()}</h3>
          <SamplesList key={`list-${current.key}`} type={current} emptyText="Этот замер ещё не записывали" />
        </Card>
      ) : (
        <Card>
          <EmptyCard title="Замеров пока нет" />
        </Card>
      )}
      <CustomMeasurementDialog open={customOpen} onClose={() => setCustomOpen(false)} />
    </div>
  )
}
